/**
 * B.H. Copyright (c) 2026 Yemot HaMashiach Ltd.
 * All Rights Reserved.
 *
 * This software is the confidential and proprietary information of
 * Yemot HaMashiach Ltd. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into
 * with Yemot HaMashiach Ltd.
 *
 * Unauthorized copying of this file, via any medium, is strictly prohibited.
 */
import React from 'react';
import { Trans } from 'react-i18next';
import Frame from '../widgets/Frame';
import QuickLogin from '../pages/QuickLogin';
import { tBrand } from '../../portalSettings';

/**
 * מסך ההתחברות בפריסה המלאה - שם משתמש, ואחריו סיסמה.
 *
 * **ציור בלבד.** כל מה שהמסך עושה מגיע ב-`view` מ-`Login`, וזה הקו שמונע משתי
 * הפריסות להיפרד: פעולה שאינה בחוזה הזה אינה יכולה להיות קיימת כאן ולא שם.
 *
 * `Frame` הוא מה שנותן את הכותרת, הלוגו ובורר השפה, וה-DOM שמתחתיו
 * (`.content` → `.main` → `.right-main`) הוא זה שכל שאר מסכי הזרימה מצוירים בו.
 */
const ClassicLoginLayout = ({ view }) => {
    const {
        t, reseller, state, hasErrors,
        showRegister, showRestoreUsername, showRestorePassword,
        quickMode, setQuickMode, quickLoginAvailable, quickCaptcha,
        captcha, submit, navigate, ivrLoginUrl,
        handleChange, handleSubmit, handleSubmitUsername, handlePreviousClick, toggleShowPassword,
    } = view;

    if (quickMode) {
        return (
            <Frame>
                <div className='main main-login'>
                    <div className='main-top'>
                        <h1>{tBrand(t, "Welcome back to the Yemot Hamessiah", reseller)}</h1>
                    </div>
                    <div className='main-body'>
                        <div className='right-main'>
                            <div className='right-main-inner'>
                                <QuickLogin
                                    resellerData={reseller}
                                    captcha={quickCaptcha}
                                    onUsePassword={() => setQuickMode(false)}
                                />
                            </div>
                        </div>
                        <div className='left-main'>
                            <img src='/images/person-phone.png' className='content-logo' alt=''/>
                        </div>
                    </div>
                </div>
            </Frame>
        );
    }

    return (
        <Frame>
            <div className='main main-login'>
                <div className='main-top'>
                    <h1>{tBrand(t, state.passwordForm?"We're almost there...":"Welcome back to the Yemot Hamessiah", reseller)}</h1>
                </div>
                <div className='main-body'>
                    <form>
                        <div className='right-main'>
                            <div className='right-main-inner'>
                                <h2>{t(state.passwordForm?"Please insert your password":"Please insert the username for login")}</h2>
                                <div className='mt10 input blue-border' style={{display:!state.passwordForm?'flex':'none'}}><input className={state.errors.username?"error":""} type="text" id="username" name="username" autoComplete="username" placeholder={t("Please insert the username")} value={state.username} onChange={(e) => handleChange(e)} /></div>
                                {state.passwordForm&&<div className='mt10 button button-space-between button-teal' onClick={(e) => handlePreviousClick(e)}><img src='/images/greater-than-white.png' className='mr20 greater-than-icon' alt=''/> {state.username} <div className='ml40'></div></div>}
                                <div className='input blue-border' style={{display:state.passwordForm?'flex':'none'}}> <input className={state.errors.password?"error":""} type="password" id="password" name="password" autoComplete="current-password" placeholder={t("Password")}  onChange={(e) => handleChange(e)} /><img src='/images/show-password-icon.png' className='input-icon ml20' onClick={(e) => toggleShowPassword(e)} alt=''/></div>

                                {/* הקאפצ'ה על שלב הסיסמה בלבד - זה השלב ששולח, והראשון אינו פונה לשרת. */}
                                {state.passwordForm && captcha.node}
                                {!state.passwordForm?<div className='button button-space-between button-teal' onClick={(e) => handleSubmitUsername(e)}> <div className='mr20'>{t("next")}</div> <img src='/images/less-than-white.png' className='ml20 button-icon less-than-icon' alt=''/> </div>
                                    :<div {...submit.buttonProps({base: 'button button-space-between button-teal'})} onClick={(e) => handleSubmit(e)}>  <div className='mr20'>{submit.busy ? t("Signing you in...") : t("next")}</div> {submit.busy ? <span className='button-spinner ml20' /> : <img src='/images/less-than-white.png' className='ml20 button-icon less-than-icon' alt=''/>} </div>}

                                {Object.entries(state.errors).map(([key, msg]) => (
                                    <h2 key={key} className='mt20 error'><Trans i18nKey={msg} /></h2>
                                ))}
                                {!state.passwordForm?<>
                                    {showRestoreUsername&&!hasErrors()?<h2 className='mt20 teal pointer' onClick={() => navigate('/restore-username')}><Trans i18nKey="Did you forget your username? <strong>Press here to restore it</strong>" /></h2>:""}
                                    {hasErrors()?<h2><Trans i18nKey="Please try again or contact our <strong>customer support</strong>" /></h2>:""}
                                    {showRegister?<h2 className='mt20 teal pointer' onClick={() => navigate('/register')}><Trans i18nKey="Don't have an account yet? <strong>Register now</strong>" /></h2>:""}
                                    {ivrLoginUrl?<h2 className='mt20 teal pointer'><a href={ivrLoginUrl} className='teal'>{t("Login with system number and password")}</a></h2>:""}
                                    {/* הדרך חזרה למהיר - בשני הכיוונים, תמיד. ריסיילר
                                        שכיבה את הפיצ'ר אינו מציג אותה כלל. */}
                                    {quickLoginAvailable?<h2 className='mt20 teal pointer' onClick={() => setQuickMode(true)}>{t("Quick login")}</h2>:""}
                                </>:(showRestorePassword?<h2 className='mt40 teal pointer' onClick={() => navigate('/reset-password')}><Trans i18nKey="Forgot your password? <strong>Press here to reset it</strong>" /></h2>:"")}
                            </div>
                        </div>
                    </form>
                    <div className='left-main'>
                        <img src='/images/person-phone.png' className='content-logo' alt=''/>
                    </div>
                </div>
            </div>
        </Frame>
    );
};

export default ClassicLoginLayout;
